import type { Shift } from '../models/data';
import { schedulerSnapshotRepository } from '../runtime/schedulerSnapshotRepository';
import {
  buildSchedulerBootstrapScopeKey,
  schedulerScopeOverlapsMutation,
  type SchedulerAffectedScope,
  isSchedulerBootstrapPayload,
  isSchedulerSingleWeekScope,
  normalizeSchedulerBootstrapUnitIds,
  type SchedulerBootstrapPayload,
  type SchedulerBootstrapScope,
} from '../scheduler/bootstrap.ts';

const SNAPSHOT_HARD_TTL_MS = 5 * 60 * 1000;
const SNAPSHOT_RETENTION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const HOT_SNAPSHOT_LIMIT = 12;
const SESSION_WARM_SNAPSHOT_LIMIT = 6;
const SESSION_WARM_AUTHORITY_LEASE_MS = 60 * 1000;

export type SchedulerSnapshotRecord = {
  scopeKey: string;
  userId: string;
  accessProfileVersion: number;
  permissionVersion: string;
  scheduleRevision: string;
  etag: string;
  savedAt: number;
  payload: SchedulerBootstrapPayload;
  /** In-memory range projection; never extends the original validation time. */
  composed?: boolean;
};

const hotSnapshots = new Map<string, SchedulerSnapshotRecord>();
const sessionWarmSnapshots = new Map<string, SchedulerSnapshotRecord>();
let hotSnapshotSessionUserId: string | null = null;
let hotSnapshotSessionAccessProfileVersion: number | null = null;
let sessionAuthorizationLease: {
  userId: string;
  accessProfileVersion: number;
  permissionVersion: string;
  validatedAt: number;
} | null = null;

const cloneRecord = (record: SchedulerSnapshotRecord): SchedulerSnapshotRecord =>
  typeof structuredClone === 'function'
    ? structuredClone(record)
    : JSON.parse(JSON.stringify(record)) as SchedulerSnapshotRecord;

const rememberHotSnapshot = (record: SchedulerSnapshotRecord): void => {
  hotSnapshots.delete(record.scopeKey);
  hotSnapshots.set(record.scopeKey, record);
  while (hotSnapshots.size > HOT_SNAPSHOT_LIMIT) {
    const oldestScopeKey = hotSnapshots.keys().next().value as string | undefined;
    if (!oldestScopeKey) break;
    hotSnapshots.delete(oldestScopeKey);
  }
};

const recordIsSingleWeek = (record: SchedulerSnapshotRecord): boolean =>
  isSchedulerSingleWeekScope({
    fromDateKey: record.payload.fromDateKey,
    toDateKey: record.payload.toDateKey,
    weekStartDateKey: record.payload.weekStartDateKey,
  });

const rememberSessionWarmSnapshot = (record: SchedulerSnapshotRecord): void => {
  if (
    !recordIsSingleWeek(record) ||
    !hotSnapshotSessionUserId ||
    hotSnapshotSessionUserId !== record.userId ||
    hotSnapshotSessionAccessProfileVersion !== record.accessProfileVersion
  ) {
    return;
  }
  sessionWarmSnapshots.delete(record.scopeKey);
  sessionWarmSnapshots.set(record.scopeKey, record);
  while (sessionWarmSnapshots.size > SESSION_WARM_SNAPSHOT_LIMIT) {
    const oldestScopeKey = sessionWarmSnapshots.keys().next().value as string | undefined;
    if (!oldestScopeKey) break;
    sessionWarmSnapshots.delete(oldestScopeKey);
  }
};

export const clearSchedulerSessionWarmSnapshots = (): void => {
  sessionWarmSnapshots.clear();
  sessionAuthorizationLease = null;
};

export const setSchedulerHotSnapshotSessionIdentity = (
  userId: string | null | undefined,
  accessProfileVersion?: number | null
): void => {
  const normalizedUserId = typeof userId === 'string' ? userId.trim() : '';
  const nextUserId = normalizedUserId || null;
  const nextAccessProfileVersion =
    typeof accessProfileVersion === 'number' && Number.isFinite(accessProfileVersion)
      ? accessProfileVersion
      : null;
  if (
    hotSnapshotSessionUserId === nextUserId &&
    hotSnapshotSessionAccessProfileVersion === nextAccessProfileVersion
  ) {
    return;
  }
  hotSnapshots.clear();
  clearSchedulerSessionWarmSnapshots();
  hotSnapshotSessionUserId = nextUserId;
  hotSnapshotSessionAccessProfileVersion = nextAccessProfileVersion;
};

export const setSchedulerSessionAuthorizationLease = ({
  userId,
  accessProfileVersion,
  permissionVersion,
  validatedAt = Date.now(),
}: {
  userId: string;
  accessProfileVersion: number;
  permissionVersion: string;
  validatedAt?: number;
}): void => {
  const normalizedUserId = userId.trim();
  const normalizedPermissionVersion = permissionVersion.trim();
  if (
    !normalizedUserId ||
    !Number.isFinite(accessProfileVersion) ||
    !normalizedPermissionVersion ||
    !Number.isFinite(validatedAt)
  ) {
    clearSchedulerSessionWarmSnapshots();
    return;
  }
  if (
    hotSnapshotSessionUserId !== normalizedUserId ||
    hotSnapshotSessionAccessProfileVersion !== accessProfileVersion
  ) {
    setSchedulerHotSnapshotSessionIdentity(normalizedUserId, accessProfileVersion);
  }
  if (
    sessionAuthorizationLease &&
    sessionAuthorizationLease.permissionVersion !== normalizedPermissionVersion
  ) {
    sessionWarmSnapshots.clear();
  }
  sessionAuthorizationLease = {
    userId: normalizedUserId,
    accessProfileVersion,
    permissionVersion: normalizedPermissionVersion,
    validatedAt,
  };
};

const normalizeOptionalScopePart = (value: string | null | undefined): string | null => {
  const normalized = typeof value === 'string' ? value.trim() : '';
  return normalized || null;
};

const isSchedulerSnapshotScopeSafe = ({
  record,
  scope,
  expectedAccessProfileVersion,
}: {
  record: SchedulerSnapshotRecord;
  scope: SchedulerBootstrapScope;
  expectedAccessProfileVersion: number | null | undefined;
}): boolean => {
  if (
    typeof expectedAccessProfileVersion !== 'number' ||
    !Number.isFinite(expectedAccessProfileVersion)
  ) {
    return false;
  }

  const scopeKey = buildSchedulerBootstrapScopeKey(scope);
  const payload = record.payload;
  if (!isSchedulerBootstrapPayload(payload)) return false;
  const expectedUnitIds = normalizeSchedulerBootstrapUnitIds(scope.unitIds);
  if (expectedUnitIds.length === 0) return false;
  const payloadUnitIds = normalizeSchedulerBootstrapUnitIds(payload.unitIds || []);
  const hasExactUnits =
    expectedUnitIds.length === payloadUnitIds.length &&
    expectedUnitIds.every((unitId, index) => unitId === payloadUnitIds[index]);
  const everyUnitRemainsReadable = expectedUnitIds.every(
    unitId =>
      payload.permissions.byUnitId?.[unitId]?.canRead === true &&
      payload.accessModeByUnitId?.[unitId] !== 'denied'
  );

  return (
    record.scopeKey === scopeKey &&
    record.userId === scope.userId &&
    record.accessProfileVersion === expectedAccessProfileVersion &&
    typeof record.etag === 'string' &&
    record.etag.length > 0 &&
    payload.scopeKey === scopeKey &&
    payload.schemaVersion === (scope.readScope ? 2 : 1) &&
    payload.readScope === scope.readScope &&
    payload.userId === scope.userId &&
    hasExactUnits &&
    normalizeOptionalScopePart(payload.divisionId) ===
      normalizeOptionalScopePart(scope.divisionId) &&
    payload.fromDateKey === scope.fromDateKey &&
    payload.toDateKey === scope.toDateKey &&
    payload.weekStartDateKey === scope.weekStartDateKey &&
    payload.noteMode === scope.noteMode &&
    payload.accessProfileVersion === expectedAccessProfileVersion &&
    payload.permissionVersion === record.permissionVersion &&
    payload.scheduleRevision === record.scheduleRevision &&
    payload.permissions.canRead === true &&
    everyUnitRemainsReadable
  );
};

export const isSchedulerSnapshotTrustedForScope = ({
  record,
  scope,
  expectedAccessProfileVersion,
  now = Date.now(),
}: {
  record: SchedulerSnapshotRecord;
  scope: SchedulerBootstrapScope;
  expectedAccessProfileVersion: number | null | undefined;
  now?: number;
}): boolean =>
  isSchedulerSnapshotScopeSafe({ record, scope, expectedAccessProfileVersion }) &&
  typeof record.savedAt === 'number' &&
  now >= record.savedAt &&
  now - record.savedAt <= SNAPSHOT_HARD_TTL_MS;

export const isSchedulerSnapshotRetainedForScope = ({
  record,
  scope,
  expectedAccessProfileVersion,
  now = Date.now(),
}: {
  record: SchedulerSnapshotRecord;
  scope: SchedulerBootstrapScope;
  expectedAccessProfileVersion: number | null | undefined;
  now?: number;
}): boolean => {
  if (
    !isSchedulerSingleWeekScope(scope) ||
    !isSchedulerSnapshotScopeSafe({ record, scope, expectedAccessProfileVersion }) ||
    typeof record.savedAt !== 'number' ||
    now < record.savedAt ||
    now - record.savedAt > SNAPSHOT_RETENTION_TTL_MS
  ) {
    return false;
  }

  const expectedUnitIds = normalizeSchedulerBootstrapUnitIds(scope.unitIds);
  return (
    record.payload.delegatedPublishedOnlyUnitIds.length === 0 &&
    expectedUnitIds.every(
      unitId => record.payload.accessModeByUnitId?.[unitId] === 'native'
    )
  );
};

export const isSchedulerSessionWarmSnapshotSafeForScope = ({
  record,
  scope,
  expectedAccessProfileVersion,
  now = Date.now(),
}: {
  record: SchedulerSnapshotRecord;
  scope: SchedulerBootstrapScope;
  expectedAccessProfileVersion: number | null | undefined;
  now?: number;
}): boolean => {
  if (
    !isSchedulerSingleWeekScope(scope) ||
    !isSchedulerSnapshotScopeSafe({ record, scope, expectedAccessProfileVersion })
  ) {
    return false;
  }
  if (
    !sessionAuthorizationLease ||
    sessionAuthorizationLease.userId !== record.userId ||
    sessionAuthorizationLease.accessProfileVersion !== record.accessProfileVersion ||
    sessionAuthorizationLease.permissionVersion !== record.permissionVersion ||
    now < sessionAuthorizationLease.validatedAt ||
    now - sessionAuthorizationLease.validatedAt > SESSION_WARM_AUTHORITY_LEASE_MS
  ) {
    return false;
  }

  const expectedUnitIds = normalizeSchedulerBootstrapUnitIds(scope.unitIds);
  return (
    record.payload.delegatedPublishedOnlyUnitIds.length === 0 &&
    expectedUnitIds.every(
      unitId => record.payload.accessModeByUnitId?.[unitId] === 'native'
    )
  );
};

export const getSchedulerSessionWarmSnapshot = ({
  scope,
  expectedAccessProfileVersion,
  now = Date.now(),
}: {
  scope: SchedulerBootstrapScope;
  expectedAccessProfileVersion: number | null | undefined;
  now?: number;
}): SchedulerSnapshotRecord | null => {
  const scopeKey = buildSchedulerBootstrapScopeKey(scope);
  const record = sessionWarmSnapshots.get(scopeKey);
  if (!record) return null;
  if (!isSchedulerSessionWarmSnapshotSafeForScope({
    record,
    scope,
    expectedAccessProfileVersion,
    now,
  })) {
    return null;
  }
  rememberSessionWarmSnapshot(record);
  return record;
};

export const getSchedulerHotSnapshot = ({
  scope,
  expectedAccessProfileVersion,
  now = Date.now(),
}: {
  scope: SchedulerBootstrapScope;
  expectedAccessProfileVersion: number | null | undefined;
  now?: number;
}): SchedulerSnapshotRecord | null => {
  const scopeKey = buildSchedulerBootstrapScopeKey(scope);
  const record = hotSnapshots.get(scopeKey);
  if (!record) return composeSchedulerRangeSnapshot({scope, expectedAccessProfileVersion, now});
  if ((record.composed && (!sessionAuthorizationLease || sessionAuthorizationLease.userId !== scope.userId || sessionAuthorizationLease.accessProfileVersion !== expectedAccessProfileVersion || sessionAuthorizationLease.permissionVersion !== record.permissionVersion || now < sessionAuthorizationLease.validatedAt || now - sessionAuthorizationLease.validatedAt >= SESSION_WARM_AUTHORITY_LEASE_MS || now - record.savedAt >= SESSION_WARM_AUTHORITY_LEASE_MS)) || !isSchedulerSnapshotTrustedForScope({
    record,
    scope,
    expectedAccessProfileVersion,
    now,
  })) {
    hotSnapshots.delete(scopeKey);
    return null;
  }
  // Map insertion order is the deterministic LRU order. Keep the original
  // payload reference so an ETag 304 does not churn the mounted scheduler.
  rememberHotSnapshot(record);
  rememberSessionWarmSnapshot(record);
  return record;
};

/** Reuse complete native published coverage, under the same short authority lease. */
function composeSchedulerRangeSnapshot({scope, expectedAccessProfileVersion, now}: {scope: SchedulerBootstrapScope; expectedAccessProfileVersion: number | null | undefined; now: number}): SchedulerSnapshotRecord | null {
  if (scope.readScope !== 'published' || scope.noteMode !== 'published' || !sessionAuthorizationLease ||
      sessionAuthorizationLease.userId !== scope.userId || sessionAuthorizationLease.accessProfileVersion !== expectedAccessProfileVersion ||
      now < sessionAuthorizationLease.validatedAt || now - sessionAuthorizationLease.validatedAt >= SESSION_WARM_AUTHORITY_LEASE_MS) return null;
  const days: string[] = [];
  for (let date = Date.parse(scope.fromDateKey), end = Date.parse(scope.toDateKey); date <= end; date += 86400000) {
    if (days.length >= 42) return null;
    days.push(new Date(date).toISOString().slice(0, 10));
  }
  if (!days.length) return null;
  const candidates = [...hotSnapshots.values(), ...sessionWarmSnapshots.values()].filter(record => {
    const payload = record.payload;
    const ownScope = {...scope, fromDateKey: payload.fromDateKey, toDateKey: payload.toDateKey, weekStartDateKey: payload.weekStartDateKey};
    return !record.composed && record.permissionVersion === sessionAuthorizationLease!.permissionVersion &&
      now >= record.savedAt && now - record.savedAt < SESSION_WARM_AUTHORITY_LEASE_MS &&
      payload.delegatedPublishedOnlyUnitIds.length === 0 && scope.unitIds.every(id => payload.accessModeByUnitId[id] === 'native') &&
      isSchedulerSnapshotTrustedForScope({record, scope: ownScope, expectedAccessProfileVersion, now});
  }).sort((a,b) => b.savedAt - a.savedAt);
  const byDay = days.map(day => candidates.find(record => record.payload.fromDateKey <= day && record.payload.toDateKey >= day));
  if (byDay.some(record => !record)) return null;
  const parts = [...new Set(byDay as SchedulerSnapshotRecord[])];
  const first = parts[0], savedAt = Math.min(...parts.map(record => record.savedAt));
  const scopeKey = buildSchedulerBootstrapScopeKey(scope);
  const scheduleRevision = `range:${parts.map(record => record.scheduleRevision).join('|')}`;
  const shifts = new Map<string, Shift>();
  const dailyContextByDateKey: SchedulerBootstrapPayload['dailyContextByDateKey'] = {};
  for (const [index, day] of days.entries()) {
    const payload = byDay[index]!.payload;
    for (const shift of payload.shifts) if (shift.dayKey === day) shifts.set(shift.id, shift);
    if (payload.dailyContextByDateKey[day]) dailyContextByDateKey[day] = payload.dailyContextByDateKey[day];
  }
  const users = [...new Map(parts.flatMap(record => record.payload.users).map(user => [user.id, user])).values()];
  const payload: SchedulerBootstrapPayload = {...first.payload, ...scope, scopeKey, scheduleRevision, shifts: [...shifts.values()], users, dailyContextByDateKey,
    pollBaselineAt: parts.map(record => record.payload.pollBaselineAt).filter((v): v is string => Boolean(v)).sort()[0],
    publicationInfoByWeek: Object.assign({}, ...parts.map(record => record.payload.publicationInfoByWeek || {})),
    weekSettingsByUnitId: byDay[0]!.payload.weekSettingsByUnitId,
    temporaryStaff: [...new Map(parts.flatMap(record => record.payload.temporaryStaff || []).map(staff => [staff.id, staff])).values()]};
  const record = {...first, payload, scopeKey, scheduleRevision, savedAt, composed: true, etag: `"local-${scheduleRevision}"`};
  if (!isSchedulerSnapshotTrustedForScope({record, scope, expectedAccessProfileVersion, now})) return null;
  rememberHotSnapshot(record);
  return record;
}

export const getSchedulerSnapshot = async ({
  scope,
  expectedAccessProfileVersion,
  now = Date.now(),
}: {
  scope: SchedulerBootstrapScope;
  expectedAccessProfileVersion: number | null | undefined;
  now?: number;
}): Promise<SchedulerSnapshotRecord | null> => {
  if (typeof expectedAccessProfileVersion !== 'number' || !Number.isFinite(expectedAccessProfileVersion)) {
    return null;
  }

  // Only same-page, server-established memory state is renderable here. Disk
  // age/APV alone cannot authorize a new page session; retained reads below
  // return candidates that require fresh server validation.
  const record = getSchedulerHotSnapshot({ scope, expectedAccessProfileVersion, now }) ||
    getSchedulerSessionWarmSnapshot({ scope, expectedAccessProfileVersion, now });
  return record ? cloneRecord(record) : null;
};

export const getSchedulerRetainedSnapshot = async ({
  scope,
  expectedAccessProfileVersion,
  now = Date.now(),
}: {
  scope: SchedulerBootstrapScope;
  expectedAccessProfileVersion: number | null | undefined;
  now?: number;
}): Promise<SchedulerSnapshotRecord | null> => {
  if (typeof expectedAccessProfileVersion !== 'number' || !Number.isFinite(expectedAccessProfileVersion)) {
    return null;
  }

  const scopeKey = buildSchedulerBootstrapScopeKey(scope);
  const record = await schedulerSnapshotRepository.read(scopeKey).catch(() => null);
  if (!record) return null;

  if (!isSchedulerSnapshotRetainedForScope({
    record,
    scope,
    expectedAccessProfileVersion,
    now,
  })) {
    await schedulerSnapshotRepository.delete(scopeKey).catch(() => undefined);
    return null;
  }

  // This is only a retained candidate. Callers must perform fresh server-side
  // authority/revision validation before rendering it.
  return cloneRecord(record);
};

export const setSchedulerSnapshot = async (
  payload: SchedulerBootstrapPayload,
  etag: string,
  now = Date.now()
): Promise<void> => {
  if (!payload.permissions.canRead || !etag) return;

  const record: SchedulerSnapshotRecord = {
    scopeKey: payload.scopeKey,
    userId: payload.userId,
    accessProfileVersion: payload.accessProfileVersion,
    permissionVersion: payload.permissionVersion,
    scheduleRevision: payload.scheduleRevision,
    etag,
    savedAt: now,
    payload,
  };
  const payloadScope: SchedulerBootstrapScope = {
    userId: payload.userId,
    unitIds: payload.unitIds,
    divisionId: payload.divisionId,
    fromDateKey: payload.fromDateKey,
    toDateKey: payload.toDateKey,
    weekStartDateKey: payload.weekStartDateKey,
    noteMode: payload.noteMode,
    readScope: payload.readScope,
  };
  if (!isSchedulerSnapshotTrustedForScope({
    record,
    scope: payloadScope,
    expectedAccessProfileVersion: payload.accessProfileVersion,
    now,
  })) return;
  rememberHotSnapshot(record);
  rememberSessionWarmSnapshot(record);
  await schedulerSnapshotRepository.write(record);
};

export const deleteSchedulerSnapshot = async (scope: SchedulerBootstrapScope): Promise<void> => {
  const scopeKey = buildSchedulerBootstrapScopeKey(scope);
  hotSnapshots.delete(scopeKey);
  sessionWarmSnapshots.delete(scopeKey);
  await schedulerSnapshotRepository.delete(scopeKey);
};


/** Retire overlapping records in every existing tier without evicting unrelated weeks. */
export const deleteSchedulerSnapshotsAffectedByMutation = async (
  userId: string, affected: SchedulerAffectedScope
): Promise<void> => {
  const matches = (record: SchedulerSnapshotRecord) => record.userId === userId &&
    schedulerScopeOverlapsMutation(record.payload, affected);
  for (const tier of [hotSnapshots, sessionWarmSnapshots]) {
    for (const [key, record] of tier) if (matches(record)) tier.delete(key);
  }
  await schedulerSnapshotRepository.removeWhere(matches);
};

export const deleteSchedulerSnapshotsForUser = async (userId: string): Promise<void> => {
  const normalizedUserId = userId.trim();
  if (!normalizedUserId) return;

  for (const [scopeKey, record] of hotSnapshots.entries()) {
    if (record.userId === normalizedUserId) hotSnapshots.delete(scopeKey);
  }
  for (const [scopeKey, record] of sessionWarmSnapshots.entries()) {
    if (record.userId === normalizedUserId) sessionWarmSnapshots.delete(scopeKey);
  }
  if (sessionAuthorizationLease?.userId === normalizedUserId) {
    sessionAuthorizationLease = null;
  }

  await schedulerSnapshotRepository.removeWhere(record => record.userId === normalizedUserId);
};

export const __schedulerHotSnapshotLimit = HOT_SNAPSHOT_LIMIT;
export const __schedulerSessionWarmSnapshotLimit = SESSION_WARM_SNAPSHOT_LIMIT;
export const __schedulerSessionWarmAuthorityLeaseMs = SESSION_WARM_AUTHORITY_LEASE_MS;
export const __getSchedulerHotSnapshotScopeKeysForTests = (): string[] =>
  Array.from(hotSnapshots.keys());
export const __getSchedulerSessionWarmScopeKeysForTests = (): string[] =>
  Array.from(sessionWarmSnapshots.keys());
export const __resetSchedulerHotSnapshotsForTests = (): void => {
  hotSnapshots.clear();
  sessionWarmSnapshots.clear();
  hotSnapshotSessionUserId = null;
  hotSnapshotSessionAccessProfileVersion = null;
  sessionAuthorizationLease = null;
};

export const __schedulerSnapshotHardTtlMs = SNAPSHOT_HARD_TTL_MS;
export const __schedulerSnapshotRetentionTtlMs = SNAPSHOT_RETENTION_TTL_MS;
export const __resetSchedulerSnapshotMemoryForTests = (): void => {
  schedulerSnapshotRepository.resetMemory();
  __resetSchedulerHotSnapshotsForTests();
};
